import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Link,
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Divider,
  useTheme,
  Tooltip,
} from '@mui/material';
import { useMediaQuery } from '@material-ui/core';
import HotelSharpIcon from '@mui/icons-material/HotelSharp';
import StarRateSharpIcon from '@mui/icons-material/StarRateSharp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import {
  POINTER_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  COLOR_YELLOW,
  COLOR_GREEN,
  INDIVIDUAL_LISTING_TOOLTIP_BUTTON_COLOR,
  INDIVIDUAL_LISTING_TOOLTIP_BUTTON_HOVER_COLOR,
  exchangeRates,
} from 'src/const';
import { InfoTooltip, DataInsightsTooltip } from '../Tooltips';
import { TooltipCard } from '.';

import {
  formatCurrency,
  convertCurrency,
  abbreviateNumber,
  checkListingRecentlyAdded,
  checkListingInactive,
} from 'src/utils/functions';
import { PointerTooltipBodyPropsType } from 'src/interfaces/iproptypes';
import { Listing } from 'src/interfaces/idashboard';
import airbnb from 'src/App/assets/airbnbMini.svg';
import { COLOR_TEXT_4 } from 'src/colors';
import {
  RecentlyAddedChip,
  RecentlyAddedClockIcon,
} from 'src/App/Components/Chips/RecentlyAddedChip';
import ListingInactivityChip from 'src/App/Components/Chips/ListingInactivityChip';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import { logAmplitudeEvent } from 'src/utils/analytics';

const styles: any = {
  contentIconStyle: {
    fontSize: 23,
    color: POINTER_GREEN,
    padding: 3,
  },
  confidenceScoreIconOuter: {
    width: 50,
    height: 50,
    borderRadius: 50,
    background: `linear-gradient(90deg, ${COLOR_RED} 0%, ${COLOR_YELLOW} 35%, ${COLOR_GREEN} 100%)`,
    position: 'absolute',
    top: 7,
    left: 7,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  confidenceScoreIconInner: {
    width: 44,
    height: 44,
    borderRadius: 44,
    background: '#123',
    cursor: 'pointer',
  },
  cardStyle: {
    position: 'relative',
    minWidth: '300px',
    maxWidth: '350px',
    overflow: 'visible',
  },
  cardBackground: {
    display: 'flex',
    minWidth: '300px',
    maxWidth: '350px',
    position: 'relative',
    overflow: 'visible',
    background: '#fff',
    borderRadius: 4,
  },
  blurText: {
    fontSize: 24,
    fontWeight: 900,
    color: POINTER_GREEN,
    filter: 'blur(5px)',
    display: 'inline-block',
  },
  mobileViewImage: {
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    border: `4px solid #eee`,
    position: 'absolute',
    top: -40,
    left: 10,
    boxShadow: '0 -4px 10px #aaa',
    background: '#fff',
  },
};

const MetricsBox = ({
  metric,
  title,
  isDisabled,
  isSmall,
  addDivider,
  demoMetrics,
  openListingModal,
}: {
  metric: string | JSX.Element;
  title: string;
  isDisabled: boolean | undefined;
  isSmall?: boolean;
  addDivider?: boolean;
  demoMetrics?: string;
  openListingModal: any;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  return (
    <Box
      sx={{
        width: '100%',
        ...(addDivider ? { borderLeft: '1px solid #ccc', borderRight: '1px solid #ccc' } : {}),
      }}
    >
      <Grid item xs={12} justifyContent={'center'} alignItems={'center'}>
        {!isDisabled ? (
          <Typography
            variant={matches ? 'h6' : 'h5'}
            sx={{ fontSize: isSmall ? '16px' : '18px', fontWeight: 'bold', textAlign: 'center' }}
          >
            {typeof metric == 'string' && metric.includes('NaN') ? 'N/A' : metric}
          </Typography>
        ) : (
          <>
            <Typography
              variant={matches ? 'h6' : 'h5'}
              sx={{
                fontSize: '20px',
                textAlign: 'center',
                filter: 'blur(3.5px)',
                cursor: 'pointer',
              }}
              onClick={openListingModal}
            >
              {/* <LockSharp sx={{ color: POINTER_GREEN }} /> */}
              {demoMetrics}
            </Typography>
          </>
        )}
      </Grid>
      <Grid item xs={12} justifyContent={'center'} alignItems={'center'}>
        <Typography
          variant={matches ? 'h6' : 'h5'}
          sx={{ fontSize: '11px', fontWeight: 'normal', textAlign: 'center' }}
        >
          {title}
        </Typography>
      </Grid>
    </Box>
  );
};

const SimplifiedAirbnbPointerBody = ({
  data,
  currency,
  canDelete,
  isDisabled,
  deleteListing,
  setOpenFullView,
  estimatorSampleListingData,
  isHidden,
  isEstimator,
  setTopPerformingListingOnFocus,
  setTopPerformingListingModalOpen,
  handleOnSampleClick,
  hiddenListingIds,
  addHiddenListingId,
  removeHiddenListingId,
  isEstimatorLocked,
  showHighResListingImage,
  disabledViewMoreButtonAction,
  isCompsSelectedFromCalculatorCompsSelection,
  handleCompsAdditionCalculatorCompsSection,
}: PointerTooltipBodyPropsType) => {
  const isMobileView = useMediaQuery('(max-width:550px)');
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const [confidanceScoreOpen, setConfidanceScoreOpen] = useState<boolean>(false);

  const isListingRecentlyAdded = checkListingRecentlyAdded(data as Listing);
  const isListingInactive = checkListingInactive(data as Listing);

  const currencySymbol = exchangeRates[currency].symbol;
  //Revenue Potential
  const revenuePotential = estimatorSampleListingData
    ? estimatorSampleListingData.revenuePotential
    : data?.['revenuePotential'];
  const formattedRevenuePotential = `${currencySymbol}${abbreviateNumber(
    convertCurrency(currency, revenuePotential)
  )}`;

  const listingUrl = estimatorSampleListingData
    ? estimatorSampleListingData.listing_url
    : `airbnb.com/rooms/${data?.['listingID']}`;

  //ADR
  let adr: number = -1;
  let formattedADR: string = '-';

  if (!estimatorSampleListingData) {
    adr = data?.['adrLTM'] ? data?.['adrLTM'] : -1;
    formattedADR =
      adr !== -1
        ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, adr)))}`
        : '-';
  }

  let dataToUse = data;

  const getTextColor = (color: string) => {
    return isHidden ? '#ccc' : color;
  };

  const getThumbnailImageUrl = (url: string) => {
    if (!url) return '';

    if (!showHighResListingImage) return url;

    try {
      return url.replace('?aki_policy=small', '');
    } catch (err) {
      return url;
    }
  };

  const HandleHideListingClick = () => {
    if (hiddenListingIds && !isEstimatorLocked) {
      if (hiddenListingIds.includes(data['listingID'])) {
        removeHiddenListingId(data['listingID']);
      } else {
        addHiddenListingId(data['listingID']);
      }
    }

    if (isEstimatorLocked) {
      handleOnSampleClick();
    }
  };

  const openListingModal = isDisabled => {
    if (disabledViewMoreButtonAction) {
      disabledViewMoreButtonAction();
      return;
    }

    isDisabled && setOpenFullView(true);
    setTopPerformingListingModalOpen && setTopPerformingListingModalOpen(data?.['listingID']);
  };

  const CompsAddButton =
    isCompsSelectedFromCalculatorCompsSelection !== undefined ? (
      <TooltipNew
        arrow={false}
        placement="bottom"
        title={
          isCompsSelectedFromCalculatorCompsSelection
            ? 'Remove this listing from the report.'
            : 'Add this listing to the report.'
        }
        content={''}
      >
        <Button
          fullWidth
          sx={{
            marginTop: '10px',
            background: COLOR_PRIMARY + (isCompsSelectedFromCalculatorCompsSelection ? 'dd' : '66'),
            color: COLOR_PRIMARY,
            borderRadius: '4px',
            '&:hover': {
              color: '#fff',
              background:
                COLOR_PRIMARY + (isCompsSelectedFromCalculatorCompsSelection ? 'aa' : '44'),
            },
          }}
          onClick={() =>
            handleCompsAdditionCalculatorCompsSection && handleCompsAdditionCalculatorCompsSection()
          }
        >
          {isCompsSelectedFromCalculatorCompsSelection ? (
            <DoneRoundedIcon sx={{ color: 'white' }} />
          ) : (
            <AddRoundedIcon sx={{ color: 'white' }} />
          )}
        </Button>
      </TooltipNew>
    ) : (
      <></>
    );

  const trackListingPageViewed = (source: string) => {
    logAmplitudeEvent('listing_modal_opened', { visited_from: source });
  };

  const OverviewDesktopPointer = () => (
    <Card
      // onMouseEnter={() => {
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => data?.['listingID']);
      // }}
      // onMouseLeave={() =>
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => null)
      // }
      sx={{
        ...styles.cardStyle,
        minWidth: '100%',
        width: '100%',
        transformOrigin: 'top left',
        transform: 'scale(1)',
      }}
    >
      <CardMedia
        component="img"
        sx={{
          width: '100%',
          height: '100px',
          objectFit: 'cover',
          borderRadius: 2,
          cursor: 'pointer',
          filter: isHidden ? 'saturate(0)' : 'saturate(1)',
          opacity: isHidden ? 0.6 : 1,
        }}
        alt={dataToUse?.['name']}
        image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
        title={dataToUse?.['name']}
        onClick={() => openListingModal(!isHidden)}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '5px',
          left: '5px',
          background: '#fff',
          padding: '4px',
          borderRadius: 200,
          width: '30px',
          height: '30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={airbnb}
          style={{
            width: '25px',
            height: '25px',
            objectFit: 'contain',
          }}
        />
      </Box>
      {isListingRecentlyAdded && (
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
          }}
        >
          <RecentlyAddedChip />
        </Box>
      )}
      {isListingInactive && (
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
          }}
        >
          <ListingInactivityChip activeDaysCount={data['activeDaysCount']} />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <CardContent sx={{ flex: '1 0 auto', paddingTop: '15px', paddingBottom: 0 }}>
          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid item xs={8}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '15px' }}
              >
                {dataToUse?.['room_type'].split('_').join(' ')} & {dataToUse?.['bedrooms']}{' '}
                {dataToUse?.['bedrooms'] == 'Studio' ? '' : 'Bed'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body1"
                sx={{ fontSize: '13px', fontWeight: 'bold', float: 'right' }}
              >
                <pre>
                  ★{dataToUse?.['review_scores_rating']} (
                  {abbreviateNumber(dataToUse?.['visible_review_count'])})
                </pre>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: '10px' }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: '14px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  overflow: 'hidden',
                  color: COLOR_TEXT_4,
                }}
              >
                {dataToUse?.['name']}
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              spacing={2}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ marginTop: '10px' }}
            >
              <Grid item xs={6}>
                <MetricsBox
                  title={'Revenue Potential'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      `${formattedRevenuePotential}`
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  demoMetrics={`${exchangeRates[currency].symbol}109`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
              <Grid item xs={6}>
                <MetricsBox
                  title={'Days Available'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      dataToUse?.['activeDaysCount'] + ''
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  demoMetrics={`300`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
            </Grid>

            <Divider component="div" sx={{ width: '100%' }} />

            <Grid
              container
              item
              xs={12}
              spacing={1}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ marginTop: '5px' }}
            >
              <Grid item xs={4}>
                <MetricsBox
                  title={'Revenue'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      `${currencySymbol}${abbreviateNumber(
                        convertCurrency(currency, data?.['annualRevenueLTM'])
                      )}`
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  isSmall={true}
                  demoMetrics={`${exchangeRates[currency].symbol}75,350`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
              <Grid item xs={4}>
                <MetricsBox
                  title={'Occupancy Rate'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      data?.['occupancyRateLTM'] + '%'
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  isSmall={true}
                  addDivider={true}
                  demoMetrics={`70%`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
              <Grid item xs={4}>
                <MetricsBox
                  title={'Daily Rate'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      `${currencySymbol}${abbreviateNumber(
                        convertCurrency(currency, data?.['adrLTM'])
                      )}`
                    )
                  }
                  isDisabled={false}
                  isSmall={true}
                  demoMetrics={`${exchangeRates[currency].symbol}96`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={() => {
                  openListingModal(!isHidden);
                  trackListingPageViewed('overview');
                }}
                sx={{
                  marginTop: '10px',
                  background: COLOR_PRIMARY + '22',
                  color: COLOR_PRIMARY,
                  borderRadius: '4px',
                  '&:hover': {
                    color: '#fff',
                    background: COLOR_PRIMARY,
                  },
                }}
              >
                View Details
              </Button>
              {CompsAddButton}
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    </Card>
  );

  const OverviewMobilePointer = () => (
    <Card
      // onMouseEnter={() => {
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => data?.['listingID']);
      // }}
      // onMouseLeave={() =>
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => null)
      // }
      sx={{ ...styles.cardStyle, maxWidth: 500 }}
    >
      <Box sx={{ ...styles.cardBackground, maxWidth: 500 }}>
        <CardMedia
          component="img"
          sx={{
            width: '30%',
            borderRadius: 2,
            cursor: 'pointer',
          }}
          alt={dataToUse?.['name']}
          image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
          title={dataToUse?.['name']}
          onClick={() => openListingModal(!isHidden)}
        />
        {isListingRecentlyAdded && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <RecentlyAddedChip />
          </Box>
        )}
        {isListingInactive && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <ListingInactivityChip activeDaysCount={data['activeDaysCount']} />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '70% !important' }}>
          <CardContent
            sx={{ flex: '1 0 auto', paddingTop: '15px', paddingBottom: '15px !important' }}
          >
            <Grid container justifyContent={'space-between'} alignItems={'center'}>
              <Grid item xs={8}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '15px' }}
                >
                  {dataToUse?.['room_type'].split('_').join(' ')} & {dataToUse?.['bedrooms']}{' '}
                  {dataToUse?.['bedrooms'] == 'Studio' ? '' : 'Bed'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '13px', fontWeight: 'bold', float: 'right' }}
                >
                  <pre>
                    ★{dataToUse?.['review_scores_rating']} (
                    {abbreviateNumber(dataToUse?.['visible_review_count'])})
                  </pre>
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '10px' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '14px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    color: COLOR_TEXT_4,
                  }}
                >
                  {dataToUse?.['name']}
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                spacing={2}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ marginTop: '10px' }}
              >
                <Grid item xs={6}>
                  <MetricsBox
                    title={'Revenue Potential'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        `${formattedRevenuePotential}`
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    demoMetrics={`${exchangeRates[currency].symbol}109`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricsBox
                    title={'Days Available'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        dataToUse?.['activeDaysCount'] + ''
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    demoMetrics={`300`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
              </Grid>

              <Divider component="div" sx={{ width: '100%' }} />

              <Grid
                container
                item
                xs={12}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ marginTop: '5px' }}
              >
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Revenue'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        `${currencySymbol}${abbreviateNumber(
                          convertCurrency(currency, data?.['annualRevenueLTM'])
                        )}`
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    isSmall={true}
                    demoMetrics={`${exchangeRates[currency].symbol}75,350`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Occupancy Rate'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        data?.['occupancyRateLTM'] + '%'
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    isSmall={true}
                    addDivider={true}
                    demoMetrics={`70%`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Daily Rate'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        `${currencySymbol}${abbreviateNumber(
                          convertCurrency(currency, data?.['adrLTM'])
                        )}`
                      )
                    }
                    isDisabled={false}
                    isSmall={true}
                    demoMetrics={`${exchangeRates[currency].symbol}96`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                onClick={() => {
                  openListingModal(!isHidden);
                  trackListingPageViewed('overview');
                }}
                sx={{
                  marginTop: '10px',
                  background: COLOR_PRIMARY + '22',
                  color: COLOR_PRIMARY,
                  borderRadius: '4px',
                  '&:hover': {
                    color: '#fff',
                    background: COLOR_PRIMARY,
                  },
                }}
              >
                View Details
              </Button>
              {CompsAddButton}
            </Grid>
          </CardContent>
        </Box>
      </Box>
    </Card>
  );

  const EstimatorPointer = () => (
    <Card sx={{ ...styles.cardStyle, maxWidth: 500 }}>
      <Box sx={{ ...styles.cardBackground, maxWidth: 500 }}>
        {isMobileView && (
          <img
            style={styles.mobileViewImage}
            alt={dataToUse?.['name']}
            src={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
            onClick={() => !isHidden && setOpenFullView(true)}
          />
        )}

        {!isMobileView && (
          <CardMedia
            component="img"
            sx={{
              width: '30%',
              borderRadius: 2,
              cursor: 'pointer',
              filter: isHidden ? 'saturate(0)' : 'saturate(1)',
              opacity: isHidden ? 0.6 : 1,
            }}
            alt={dataToUse?.['name']}
            image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
            title={dataToUse?.['name']}
            onClick={() => !isHidden && setOpenFullView(true)}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '500px !important' }}>
          <CardContent sx={{ flex: '1 0 auto', paddingTop: '25px' }}>
            <Typography
              component="div"
              variant="h5"
              sx={{
                fontSize: isMobileView ? '16px' : '18px',
                fontWeight: 'bold',
                pb: 1,
                color: getTextColor('#000'),
              }}
            >
              <Link
                href={'https://www.' + listingUrl}
                target="_blank"
                rel="nofollow"
                color="inherit"
                underline="none"
              >
                {dataToUse?.['name']}
              </Link>
              <span style={{ color: getTextColor(POINTER_GREEN) }}> by </span>
              <Link
                href={'https://www.airbnb.com/users/show/' + dataToUse?.['hostId']}
                target="_blank"
                rel="nofollow"
                color={getTextColor(POINTER_GREEN)}
              >
                {dataToUse?.['host_name']}
              </Link>
            </Typography>
            <Typography
              component={'span'}
              color={getTextColor('textSecondary')}
              sx={{ fontSize: isMobileView ? '12px' : '14px' }}
            >
              <Grid container>
                <Grid item xs={2}>
                  <HotelSharpIcon
                    style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                  />{' '}
                  {dataToUse?.['bedrooms']}
                </Grid>

                <Grid item xs={10}>
                  {dataToUse?.['review_scores_rating'] > 0 ? (
                    <StarRateSharpIcon
                      style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                    />
                  ) : (
                    ''
                  )}
                  {dataToUse?.['review_scores_rating'] > 0
                    ? dataToUse?.['review_scores_rating']
                    : ''}

                  {dataToUse?.['review_scores_rating'] > 0 && (
                    <span style={{ marginLeft: '10px', marginRight: '4px' }}>
                      ( {dataToUse?.['visible_review_count']}{' '}
                      {dataToUse?.['visible_review_count'] > 1 ? 'reviews' : 'review'} )
                    </span>
                  )}

                  {isMobileView &&
                    dataToUse?.['confidenceScoreRelatedData'] &&
                    `(Data score: ${Math.floor(
                      dataToUse?.['confidenceScoreRelatedData'].dataQualityScore
                    )})`}
                </Grid>
              </Grid>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
              <Typography
                color={getTextColor('textPrimary')}
                variant="subtitle2"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Revenue Potential{' '}
                <InfoTooltip
                  title={
                    <TooltipCard
                      content={
                        'It includes Revenue Potential (The annual revenue under the assumption that the listing is available for 365 days) of this listing. Click on the view details button below in order to see other details.'
                      }
                    />
                  }
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: '16px',
                      color: getTextColor(COLOR_PRIMARY),
                      cursor: 'pointer',
                      marginLeft: '10px',
                    }}
                  />
                </InfoTooltip>
              </Typography>
            </Box>
            <Grid sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Typography
                color="textPrimary"
                variant="h6"
                alignItems="flex-start"
                style={{
                  color: getTextColor(POINTER_GREEN),
                  fontSize: isMobileView ? '22px' : '28px',
                  fontWeight: 900,
                }}
              >
                {revenuePotential ? (
                  `${formattedRevenuePotential}`
                ) : (
                  <>
                    <span
                      style={{
                        color: getTextColor(POINTER_GREEN),
                        fontSize: isMobileView ? '22px' : '28px',
                        fontWeight: 900,
                      }}
                    >
                      {currencySymbol}
                    </span>
                    <span style={styles.blurText}>11,111</span>
                  </>
                )}{' '}
                <span
                  style={{
                    color: getTextColor('#555'),
                    fontSize: isMobileView ? '16px' : '20px',
                    fontWeight: 700,
                  }}
                >
                  /year
                </span>
                <br />
              </Typography>

              {(!isDisabled || isHidden) && (
                <Grid item xs={2}>
                  {(canDelete === true || canDelete === undefined) && (
                    <InfoTooltip
                      title={
                        <TooltipCard content="By removing this listing, it won't be included in the calculation for the profit simulation, and rest of the charts." />
                      }
                    >
                      <IconButton
                        onClick={() => {
                          HandleHideListingClick();
                        }}
                      >
                        {isEstimator ? <></> : <DeleteIcon style={{ color: COLOR_RED }} />}
                      </IconButton>
                    </InfoTooltip>
                  )}
                </Grid>
              )}
            </Grid>

            {isDisabled && !estimatorSampleListingData && !revenuePotential && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: 1,
                  width: '100%',
                }}
              >
                <Grid justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    ADR (Average Daily Rate) <br />
                  </Typography>
                  <Typography
                    color="textPrimary"
                    variant="h6"
                    alignItems="flex-start"
                    style={{
                      color: getTextColor(POINTER_GREEN),
                      fontSize: isMobileView ? '18px' : '22px',
                      fontWeight: 900,
                    }}
                  >
                    {formattedADR}
                    <br />
                  </Typography>
                </Grid>
              </Box>
            )}
          </CardContent>
          <Grid sx={{ width: '100%' }}>
            <Button
              variant="contained"
              sx={{
                position: 'absolute',
                bottom: -20,
                right: 25,
                background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_COLOR),
                '&:hover': {
                  background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_HOVER_COLOR),
                  cursor: 'pointer',
                },
              }}
              onClick={() => {
                !isHidden && setOpenFullView(true);
                trackListingPageViewed('estimator');
              }}
            >
              View Details
            </Button>
          </Grid>
        </Box>
      </Box>
    </Card>
  );

  return isEstimator ? (
    <EstimatorPointer />
  ) : matches ? (
    <OverviewMobilePointer />
  ) : (
    <OverviewDesktopPointer />
  );
};

export default React.memo(SimplifiedAirbnbPointerBody);
